export function createDocumentQueries({ defineProjectQuery, projectZql, }) {
    if (!defineProjectQuery) {
        throw new Error("Missing document query dependencies");
    }
    return {
        studioDocuments: {
            all: defineProjectQuery(({ ctx }) => {
                if (ctx.role !== "admin" && ctx.role !== "employee") {
                    return projectZql.studioDocuments.where("id", "__unauthorized__");
                }
                return projectZql.studioDocuments
                    .related("document")
                    .related("studio")
                    .orderBy("title", "asc");
            }),
            byStudio: defineProjectQuery(({ args, ctx, }) => {
                if (ctx.role !== "admin" && ctx.role !== "employee") {
                    return projectZql.studioDocuments.where("id", "__unauthorized__");
                }
                return projectZql.studioDocuments
                    .where("studioID", args.studioID)
                    .related("document")
                    .related("studio")
                    .orderBy("title", "asc");
            }),
        },
        globalDocuments: {
            all: defineProjectQuery(({ ctx }) => {
                if (ctx.role !== "admin" && ctx.role !== "employee") {
                    return projectZql.globalDocuments.where("id", "__unauthorized__");
                }
                return projectZql.globalDocuments
                    .related("document")
                    .related("links")
                    .orderBy("sortOrder", "asc")
                    .orderBy("title", "asc");
            }),
            list: defineProjectQuery(({ ctx }) => {
                if (ctx.role !== "admin" && ctx.role !== "employee") {
                    return projectZql.globalDocuments.where("id", "__unauthorized__");
                }
                return projectZql.globalDocuments
                    .where("hiddenFromDocs", false)
                    .related("document")
                    .related("links")
                    .orderBy("sortOrder", "asc")
                    .orderBy("title", "asc");
            }),
        },
        globalDocumentLinks: {
            byEntity: defineProjectQuery(({ args, ctx, }) => {
                if (ctx.role !== "admin" && ctx.role !== "employee") {
                    return projectZql.globalDocumentLinks.where("id", "__unauthorized__");
                }
                return projectZql.globalDocumentLinks
                    .where("linkType", args.linkType)
                    .where("linkID", args.linkID)
                    .orderBy("createdAt", "asc");
            }),
            byDocument: defineProjectQuery(({ args, ctx, }) => {
                if (ctx.role !== "admin" && ctx.role !== "employee") {
                    return projectZql.globalDocumentLinks.where("id", "__unauthorized__");
                }
                return projectZql.globalDocumentLinks
                    .where("globalDocumentID", args.documentID)
                    .orderBy("createdAt", "asc");
            }),
        },
    };
}
export function createDocumentMutators({ defineProjectMutator, projectZql, schemas, assertAdminDataWriteAccess, }) {
    if (!defineProjectMutator || !schemas || !assertAdminDataWriteAccess) {
        throw new Error("Missing document mutator dependencies");
    }
    return {
        studioDocuments: {
            create: defineProjectMutator(schemas.createStudioDocumentInputSchema, async ({ tx, ctx, args, }) => {
                assertAdminDataWriteAccess(ctx);
                const now = Date.now();
                await tx.mutate.documents.insert({
                    id: args.documentID,
                    createdAt: now,
                    updatedAt: now,
                });
                await tx.mutate.studioDocuments.insert({
                    id: args.id,
                    title: args.title,
                    studioID: args.studioID,
                    documentID: args.documentID,
                    createdBy: ctx.userID ?? null,
                    createdAt: now,
                    updatedAt: now,
                });
            }),
            update: defineProjectMutator(schemas.updateStudioDocumentInputSchema, async ({ tx, ctx, args, }) => {
                assertAdminDataWriteAccess(ctx);
                await tx.mutate.studioDocuments.update({
                    id: args.id,
                    title: args.title,
                    updatedAt: Date.now(),
                });
            }),
            delete: defineProjectMutator(schemas.studioDocumentIDInputSchema, async ({ tx, ctx, args, }) => {
                assertAdminDataWriteAccess(ctx);
                const doc = await tx.run(projectZql.studioDocuments.where("id", args.id).one());
                await tx.mutate.studioDocuments.delete({ id: args.id });
                if (doc?.documentID) {
                    await tx.mutate.documents.delete({ id: doc.documentID });
                }
            }),
        },
        globalDocuments: {
            create: defineProjectMutator(schemas.createGlobalDocumentInputSchema, async ({ tx, ctx, args, }) => {
                assertAdminDataWriteAccess(ctx);
                const now = Date.now();
                const documentID = args.isFolder ? null : args.documentID;
                if (documentID) {
                    await tx.mutate.documents.insert({
                        id: documentID,
                        createdAt: now,
                        updatedAt: now,
                    });
                }
                await tx.mutate.globalDocuments.insert({
                    id: args.id,
                    title: args.title,
                    parentID: args.parentID ?? null,
                    isFolder: args.isFolder ?? false,
                    hiddenFromDocs: args.hiddenFromDocs ?? false,
                    documentID,
                    sortOrder: 0,
                    createdBy: ctx.userID ?? null,
                    createdAt: now,
                    updatedAt: now,
                });
            }),
            update: defineProjectMutator(schemas.updateGlobalDocumentInputSchema, async ({ tx, ctx, args, }) => {
                assertAdminDataWriteAccess(ctx);
                await tx.mutate.globalDocuments.update({
                    ...args,
                    updatedAt: Date.now(),
                });
            }),
            delete: defineProjectMutator(schemas.globalDocumentIDInputSchema, async ({ tx, ctx, args, }) => {
                assertAdminDataWriteAccess(ctx);
                const doc = await tx.run(projectZql.globalDocuments.where("id", args.id).one());
                await tx.mutate.globalDocuments.delete({ id: args.id });
                if (doc?.documentID) {
                    await tx.mutate.documents.delete({ id: doc.documentID });
                }
            }),
        },
        globalDocumentLinks: {
            create: defineProjectMutator(schemas.createGlobalDocumentLinkInputSchema, async ({ tx, ctx, args, }) => {
                assertAdminDataWriteAccess(ctx);
                const existing = await tx.run(projectZql.globalDocumentLinks
                    .where("globalDocumentID", args.globalDocumentID)
                    .where("linkType", args.linkType)
                    .where("linkID", args.linkID)
                    .one());
                if (existing)
                    return;
                await tx.mutate.globalDocumentLinks.insert({
                    ...args,
                    createdAt: Date.now(),
                });
            }),
            delete: defineProjectMutator(schemas.deleteGlobalDocumentLinkInputSchema, async ({ tx, ctx, args, }) => {
                assertAdminDataWriteAccess(ctx);
                await tx.mutate.globalDocumentLinks.delete({ id: args.id });
            }),
        },
    };
}
